import { Button, Empty, message, Spin } from 'antd';
import { AppLinkComp, MobileHeaderComp, PostCardComp } from 'components';
import { PATH } from 'data';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { TbSquarePlus } from 'react-icons/tb';
import { useRealTimeState, useThen } from 'scripts';
import { __LoadedPage, __PageQueryKey } from 'scripts/paginationQuery';
import { PaginationType } from 'types';
import Styles from './myViews.module.scss';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { InView } from 'react-intersection-observer';
import { __FetchMyViews, __MY_VIEWS_PAGE_SIZE, __MyViewItemType } from './myViews.script';

function MyViewedComp() {
    const queryKey = useMemo(() => ['posts', 'myViews'] as const, []);
    const pageQueryKey = useMemo(() => __PageQueryKey(queryKey), [queryKey]);
    const queryClient = useQueryClient();
    const [page, setPageState] = useState(
        () =>
            queryClient.getQueryData<number>(pageQueryKey) ??
            __LoadedPage(queryClient.getQueryData<PaginationType<__MyViewItemType>>(queryKey), __MY_VIEWS_PAGE_SIZE) ??
            1,
    );
    const setPage = useCallback(
        (value: React.SetStateAction<number>) => {
            setPageState((prev) => {
                const next = typeof value === 'function' ? value(prev) : value;
                queryClient.setQueryData(pageQueryKey, next);
                return next;
            });
        },
        [queryClient, pageQueryKey],
    );
    const query = useQuery({
        queryKey,
        queryFn: () => {
            const prev = page > 1 ? queryClient.getQueryData<PaginationType<__MyViewItemType>>(queryKey) : undefined;
            return __FetchMyViews(page, prev);
        },
        staleTime: 30 * 60 * 1000,
    });
    const loadMore = useRealTimeState(false);

    useThen(() => {
        queryClient
            .cancelQueries({ queryKey })
            .then(() => {
                if (page === 1) {
                    queryClient.setQueryData(queryKey, undefined);
                    queryClient.setQueryData(pageQueryKey, 1);
                } else loadMore.set(true);
                return query.refetch();
            })
            .catch(() => message.error('دریافت آگهی‌ها با خطا مواجه شد. لطفا بعدا تلاش کنید.'))
            .finally(() => loadMore.set(false));
    }, [page]);

    const posts = query.data;

    return (
        <>
            <MobileHeaderComp title="بازدید های اخیر" />
            {!posts ? (
                <div className={`w-100 pv-9 center-content flex-1`}>
                    <Spin />
                </div>
            ) : posts.count ? (
                <div className={`${Styles['main-content']} flex-column flex-between`}>
                    <div className={`${Styles['ads-layout']}`}>
                        {posts.data.map((item, index) => (
                            <Fragment key={index}>
                                {posts.num_of_pages !== page && index >= posts.data.length - 6 ? (
                                    <InView
                                        as="div"
                                        onChange={(inView) => {
                                            if (inView && !loadMore.get()) setPage(page + 1);
                                        }}
                                    >
                                        <PostCardComp data={item.post} />
                                    </InView>
                                ) : (
                                    <PostCardComp data={item.post} />
                                )}
                            </Fragment>
                        ))}
                        {Array.from({ length: 3 }).map((_, index) => (
                            <div key={index} />
                        ))}
                    </div>
                    {posts.num_of_pages !== page ? (
                        <InView
                            as="div"
                            className="center-content pv-2 w-100"
                            onChange={(inView) => {
                                if (inView && !loadMore.get()) setPage(page + 1);
                            }}
                        >
                            <Spin />
                        </InView>
                    ) : null}
                </div>
            ) : (
                <Empty
                    description="تا کنون بازدیدی برای شما ثبت نشده است"
                    className={`${Styles['empty']} center-content flex-column`}
                >
                    <AppLinkComp href={PATH.posts()}>
                        <Button icon={<TbSquarePlus size={20} />}>{'جست و جو در آگهی ها'}</Button>
                    </AppLinkComp>
                </Empty>
            )}
        </>
    );
}

export default MyViewedComp;
